import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import HeroSection from '../components/HeroSection';
import SEO from '../components/SEO';

const TherapiesPage = () => {
  // Get data from global context
  const { therapies, loading } = useData();

  if (loading.therapies) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading therapies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <SEO
        pageType="therapy"
        title="Holistic Therapies | Acupuncture & Chiropractic Treatments | JIVA NADI"
        description="Discover our comprehensive range of holistic healing therapies including acupuncture, chiropractic care, and traditional treatments for natural pain relief."
        keywords="acupuncture therapy, chiropractic treatments, holistic healing, pain relief, traditional medicine, Mumbai therapies"
      />

      {/* Reusable Hero Section */}
      <HeroSection
        title="Discover Your Path to Natural Healing"
        subtitle="Holistic Healing Therapies"
        description="Experience our comprehensive range of evidence-based therapies combining ancient wisdom with modern science for optimal health and wellness."
        image="https://images.unsplash.com/photo-1631217314830-4e81b5e0e6e3?w=800&h=600&fit=crop"
        badge="Holistic Healing Therapies"
        primaryButton={{
          text: "Explore Therapies",
          href: "#therapies"
        }}
        secondaryButton={{
          text: "Book Consultation",
          href: "/contact"
        }}
        trustIndicators={[
          { value: "16+", label: "Therapy Types" },
          { value: "15+", label: "Years Experience" },
          { value: "30K+", label: "Happy Patients" }
        ]}
      />

      {/* Therapies Grid - Premium Redesign */}
      <div id="therapies" className="bg-gradient-to-b from-white via-emerald-50/30 to-white py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(16,185,129,0.3)_1px,transparent_0)] bg-[length:24px_24px]"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-100/80 text-emerald-700 text-sm font-semibold mb-6 backdrop-blur-sm border border-emerald-200/50">
              <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse"></span>
              Our Therapies
            </div>
            <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-6 leading-tight">
              Comprehensive Healing Therapies
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Explore our complete range of evidence-based therapies designed to restore your health and vitality
            </p>
          </div>

          {therapies.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Therapies Available</h3>
              <p className="text-gray-600">Please check back later or contact us for more information.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-8">
              {therapies.map((therapy, index) => (
                <Link
                  key={therapy.id}
                  to={therapy.path}
                  className="group block h-full bg-white rounded-3xl p-8 shadow-sm border border-gray-100/80 hover:shadow-xl hover:shadow-emerald-100/50 hover:border-emerald-200/60 transition-all duration-500 hover:-translate-y-2 relative overflow-hidden"
                >
                  {/* Subtle gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/0 to-emerald-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl"></div>

                  {/* Therapy Icon/Image */}
                  <div className="relative z-10 mb-6">
                    <div className="w-20 h-20 mx-auto rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-emerald-200/50 transition-all duration-500 group-hover:scale-110">
                      <img
                        src={therapy.image}
                        alt={therapy.title}
                        className="w-16 h-16 object-cover rounded-xl"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=200&h=200&fit=crop&crop=center';
                        }}
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="relative z-10 text-center">
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-emerald-700 transition-colors duration-300 mb-3 leading-tight">
                      {therapy.title}
                    </h3>

                    <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-2">
                      {therapy.tagline || "Experience holistic healing through traditional and modern therapeutic approaches."}
                    </p>

                    {/* Explore indicator */}
                    <div className="flex items-center justify-center gap-2 text-emerald-600 group-hover:text-emerald-700 transition-colors duration-300">
                      <span className="text-sm font-semibold">Learn More</span>
                      <svg
                        className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>

                  {/* Decorative element */}
                  <div className="absolute top-4 right-4 w-8 h-8 bg-emerald-100/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 py-12 px-4 md:px-8">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Transform Your Health?</h2>
          <p className="text-lg mb-8 opacity-95">Book a consultation with Dr. Saurabh Singh to find the right therapy for your needs</p>
          <a
            href="/contact"
            className="inline-block bg-white text-emerald-600 font-bold py-3 px-8 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Book Consultation
          </a>
        </div>
      </div>
    </div>
  );
};

export default TherapiesPage;
